import { apiClient } from './client';

/**
 * The standing IT Hardware project, one row per financial year (085, §21).
 *
 * ⛔ IT USED TO BE AN APP SETTING. Anthony, 02/10/2026: *"this must be done from
 * UI as it will be needed for future years."* An app setting meant an Azure CLI
 * command, a function app restart and a trip to IT every July.
 *
 * ⛔ ONE ROW PER YEAR, NOT ONE VALUE. A single editable value still has to be
 * changed ON THE DAY — miss it and every purchase from 1 July quietly charges
 * last year's project. Next year's can be set whenever, and the rollover happens
 * by itself.
 */

export interface HardwareCostProject {
  /** ⚠ The ENDING year: 2027 is 1 July 2026 – 30 June 2027. */
  FinancialYear: number;
  ProjectReference: string;
  /** ⚠ A snapshot of what it was called when it was chosen. */
  ProjectName: string | null;
  Note: string | null;
  ModifiedBy: string | null;
  ModifiedAtUtc: string | null;
}

export interface HardwareCostProjectsResponse {
  /**
   * ⛔ COMPUTED SERVER-SIDE, in Sydney local time. Do not re-derive it from the
   * browser clock: between midnight and 10am Sydney on 1 July, a UTC browser
   * would show the old year while a purchase charged the new one.
   */
  CurrentFinancialYear: number;
  Years: HardwareCostProject[];
}

/**
 * GET /api/hardware-cost-projects
 *
 * ⚠ An empty `Years` is survivable and means "nothing is set" — the API returns
 * one rather than a 500 when the table is missing. Do not treat it as an error.
 */
export const getHardwareCostProjects = (signal?: AbortSignal) =>
  apiClient.get<HardwareCostProjectsResponse>('/api/hardware-cost-projects', signal);

/**
 * PUT /api/hardware-cost-projects/:year — set or change one year. Admin only.
 *
 * ⚠ An empty `ProjectReference` CLEARS the year (a soft delete server-side)
 * rather than storing a blank, because a row holding "" resolves to nothing
 * while reading as configured.
 */
export const setHardwareCostProject = (
  year: number,
  body: { ProjectReference: string; ProjectName?: string | null; Note?: string | null },
) => apiClient.put<{ saved: boolean; financialYear: number; projectReference: string | null }>(
  `/api/hardware-cost-projects/${year}`, body);
